import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Tag {
  id: string;
  name: string;
  color?: string;
  icon?: string;
  organization_id?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  usage_count?: number;
}

interface TagBadgeProps {
  tag: Tag;
  onRemove?: (tagId: string) => void;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

/**
 * Display a single tag as a colored badge
 */
export function TagBadge({ tag, onRemove, size = 'sm', className }: TagBadgeProps) {
  const sizeClasses = {
    xs: 'text-xs px-2 py-0.5',
    sm: 'text-sm px-2.5 py-0.5',
    md: 'text-base px-2.5 py-0.5',
  };

  const backgroundColor = tag.color || '#6B7280';
  const textColor = getContrastColor(backgroundColor);
  const borderColor = tag.color || '#6B7280';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-medium',
        sizeClasses[size],
        className
      )}
      style={{ backgroundColor, color: textColor, borderColor }}
    >
      {tag.icon && <span className="text-sm">{tag.icon}</span>}
      <span>{tag.name}</span>
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(tag.id);
          }}
          className="ml-1 hover:opacity-70 transition-opacity"
          aria-label={`Remove ${tag.name} tag`}
          type="button"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

/**
 * Calculate contrast color for text (white or black) based on background
 */
function getContrastColor(hexColor: string): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
